// Firebase configuration - same as mobile app
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, getDoc, serverTimestamp, limit, addDoc, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { authenticator } from 'otplib';
import QRCode from 'qrcode';
import { createHash } from 'crypto-browserify';
export { getDoc, doc } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyD-ZMxUauejBsAVDSWVmR_yYZWNpAtYGvE",
  authDomain: "mymobileapp-e7e8b.firebaseapp.com",
  projectId: "mymobileapp-e7e8b",
  storageBucket: "mymobileapp-e7e8b.firebasestorage.app",
  messagingSenderId: "422038749479",
  appId: "1:422038749479:web:134921a4b7748303d6a902",
  measurementId: "G-SP82MYF4CG"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
// Functions instance for asia-southeast1 (used by Billplz payout to avoid IP-based review delays)
const functionsAsia = getFunctions(app, 'asia-southeast1');

// Auth functions
export const adminSignIn = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);

  // SECURITY: Check admin role via custom claims (not Firestore field which can be self-set)
  const idTokenResult = await userCredential.user.getIdTokenResult();
  if (!idTokenResult.claims.admin) {
    await signOut(auth);
    throw new Error('Access denied: Not an admin');
  }

  return userCredential.user;
};

export const adminSignOut = () => signOut(auth);

// ID Verification functions - Get ALL verifications (not just manual_review)
export const getPendingVerifications = async () => {
  const q = query(collection(db, 'idVerifications'));
  const snapshot = await getDocs(q);
  const verifications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by most recent first
  verifications.sort((a, b) => {
    const aTime = a.submittedAt?.seconds || 0;
    const bTime = b.submittedAt?.seconds || 0;
    return bTime - aTime;
  });

  return verifications;
};

export const approveVerification = async (verificationId, adminUserId, notes = '') => {
  const verificationRef = doc(db, 'idVerifications', verificationId);
  const verificationSnap = await getDoc(verificationRef);

  if (!verificationSnap.exists()) throw new Error('Verification not found');

  const verificationData = verificationSnap.data();

  await updateDoc(verificationRef, {
    status: 'admin_approved',
    reviewedBy: adminUserId,
    reviewedAt: serverTimestamp(),
    adminNotes: notes
  });

  // Update user profile
  await updateDoc(doc(db, 'users', verificationData.userId), {
    idVerified: true,
    idVerifiedAt: serverTimestamp(),
    idType: verificationData.idType,
    icNumber: verificationData.extractedData?.icNumber || verificationData.extractedData?.passportNumber
  });
};

export const rejectVerification = async (verificationId, adminUserId, reason, notes = '') => {
  await updateDoc(doc(db, 'idVerifications', verificationId), {
    status: 'admin_rejected',
    reviewedBy: adminUserId,
    reviewedAt: serverTimestamp(),
    rejectionReason: reason,
    adminNotes: notes
  });
};

// Job monitoring functions
export const getActiveJobs = async () => {
  const q = query(collection(db, 'activeAssignments'));
  const snapshot = await getDocs(q);
  const jobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by most recent first (createdAt or startTime)
  jobs.sort((a, b) => {
    const aTime = a.createdAt?.seconds || a.startTime?.seconds || 0;
    const bTime = b.createdAt?.seconds || b.startTime?.seconds || 0;
    return bTime - aTime;
  });

  return jobs;
};

export const getPostedJobs = async () => {
  const q = query(collection(db, 'staffingRequests'));
  const snapshot = await getDocs(q);
  const jobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by most recent first
  jobs.sort((a, b) => {
    const aTime = a.createdAt?.seconds || a.postedAt?.seconds || 0;
    const bTime = b.createdAt?.seconds || b.postedAt?.seconds || 0;
    return bTime - aTime;
  });

  return jobs;
};

// Commission calculation (10% platform fee)
export const calculateCommission = (assignments) => {
  return assignments.reduce((total, assignment) => {
    const jobTotal = (assignment.hourlyRate || 0) * (assignment.duration || 0);
    return total + (jobTotal * 0.10); // 10% commission
  }, 0);
};

// Payout management functions
export const getReleasedPayouts = async () => {
  // Fetch all RELEASED escrow payments (ready for payout)
  const q = query(collection(db, 'escrowPayments'), where('status', '==', 'released'));
  const snapshot = await getDocs(q);
  const payouts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by release date (oldest first - FIFO payout)
  payouts.sort((a, b) => {
    const aTime = a.releasedAt?.seconds || 0;
    const bTime = b.releasedAt?.seconds || 0;
    return aTime - bTime;
  });

  return payouts;
};

export const getDisputedPayouts = async () => {
  // Fetch all DISPUTED escrow payments (require admin review)
  const q = query(collection(db, 'escrowPayments'), where('status', '==', 'disputed'));
  const snapshot = await getDocs(q);
  const disputes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by dispute date (oldest first)
  disputes.sort((a, b) => {
    const aTime = a.disputedAt?.seconds || 0;
    const bTime = b.disputedAt?.seconds || 0;
    return aTime - bTime;
  });

  return disputes;
};

export const markPayoutAsPaid = async (escrowId, adminUserId, payoutDetails) => {
  // Mark payment as paid (after manual bank transfer)
  await updateDoc(doc(db, 'escrowPayments', escrowId), {
    status: 'paid',
    paidToWorkerAt: serverTimestamp(),
    paidBy: adminUserId,
    payoutMethod: payoutDetails.method || 'bank_transfer',
    payoutReference: payoutDetails.reference || null,
    payoutNotes: payoutDetails.notes || null,
    updatedAt: serverTimestamp()
  });
};

// ===== CHORE PAYOUTS (sidecar — released by OTP+OCR, not admin review) =====
// Chores have their own escrow collection (choreEscrows) so tender + chore
// money flows never mix. Mirrors the tender payout shape so the admin
// payouts UI can render both lists together.
export const getChoreReleasedPayouts = async () => {
  const q = query(collection(db, 'choreEscrows'), where('status', '==', 'RELEASED'));
  const snapshot = await getDocs(q);
  const rows = [];
  for (const d of snapshot.docs) {
    const e = d.data();
    // Pull worker bank + name + chore title in parallel (best-effort)
    let workerName = 'Unknown';
    let bankDetails = null;
    let choreTitle = 'Chore';
    if (e.runnerId) {
      const u = await getDoc(doc(db, 'users', e.runnerId));
      if (u.exists()) {
        const ud = u.data();
        workerName = ud.displayName || ud.firstName || 'Unknown';
        bankDetails = ud.bankDetails || null;
      }
    }
    if (e.choreId) {
      const c = await getDoc(doc(db, 'choreRequests', e.choreId));
      if (c.exists()) choreTitle = c.data().title || 'Chore';
    }
    rows.push({
      id: d.id,
      escrowId: d.id,
      choreId: e.choreId,
      jobTitle: choreTitle,
      workerId: e.runnerId,
      workerName,
      amount: e.totalEscrow,
      workerPayout: e.serviceFee,
      platformFee: (e.platformCommission || 0) + (e.platformFlatFee || 0),
      releasedAt: e.releasedAt,
      bankDetails,
      paymentMethod: 'billplz_fpx',
      status: 'released',
      source: 'chore', // distinguishes from tender escrowPayments
    });
  }
  rows.sort((a, b) => (a.releasedAt?.seconds || 0) - (b.releasedAt?.seconds || 0));
  return rows;
};

export const markChorePayoutAsPaid = async (escrowId, adminUserId, payoutDetails) => {
  // Updates choreEscrows + choreRequests + choreAssignments to PAID.
  // Direct Firestore writes (admin SDK rules grant admins write access).
  await updateDoc(doc(db, 'choreEscrows', escrowId), {
    status: 'PAID',
    paidAt: serverTimestamp(),
    paidBy: adminUserId,
    payoutMethod: payoutDetails.method || 'bank_transfer',
    payoutReference: payoutDetails.reference || null,
    payoutNotes: payoutDetails.notes || null,
    updatedAt: serverTimestamp()
  });
};

// Dispute resolution functions
export const releaseDisputedPayment = async (escrowId, adminUserId, notes = '') => {
  // Admin approves worker - release payment despite dispute
  await updateDoc(doc(db, 'escrowPayments', escrowId), {
    status: 'released',
    releasedAt: serverTimestamp(),
    resolvedBy: adminUserId,
    resolutionNotes: notes,
    resolutionType: 'approve_worker',
    disputeResolved: true,
    updatedAt: serverTimestamp()
  });
};

export const refundDisputedPayment = async (escrowId, adminUserId, notes = '') => {
  // Admin approves business - refund payment
  await updateDoc(doc(db, 'escrowPayments', escrowId), {
    status: 'refunded',
    refundedAt: serverTimestamp(),
    refundedBy: adminUserId,
    refundNotes: notes,
    resolutionType: 'refund_business',
    disputeResolved: true,
    updatedAt: serverTimestamp()
  });
};

// Hold payment for review (prevents accidental payout)
export const holdPayment = async (escrowId, adminUserId, reason = '') => {
  await updateDoc(doc(db, 'escrowPayments', escrowId), {
    onHold: true,
    holdReason: reason,
    holdBy: adminUserId,
    holdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Release hold on payment (allows payout again)
export const releaseHold = async (escrowId, adminUserId) => {
  await updateDoc(doc(db, 'escrowPayments', escrowId), {
    onHold: false,
    holdReleasedBy: adminUserId,
    holdReleasedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Get payouts on hold
export const getHeldPayouts = async () => {
  const q = query(
    collection(db, 'escrowPayments'),
    where('status', '==', 'released'),
    where('onHold', '==', true)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// ===== RULE-BASED SYSTEM FUNCTIONS (Not Hardcoded) =====

// Platform Configuration Management
export const getPlatformConfig = async (configType) => {
  const q = query(collection(db, 'platformConfig'), where('type', '==', configType), where('active', '==', true));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getAllPlatformConfig = async () => {
  const q = query(collection(db, 'platformConfig'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updatePlatformConfig = async (configId, updates) => {
  await updateDoc(doc(db, 'platformConfig', configId), {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

// Workflow Management
export const getWorkflows = async () => {
  const q = query(collection(db, 'platformConfig'), where('type', '==', 'workflow'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getWorkflowExecutions = async (maxResults = 100) => {
  // Note: orderBy requires a composite index if combined with other filters
  // For now, fetch more documents and sort client-side to ensure we get recent ones
  const q = query(collection(db, 'workflowExecutions'));
  const snapshot = await getDocs(q);
  const executions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by most recent first
  executions.sort((a, b) => {
    const aTime = a.startedAt?.seconds || a.createdAt?.seconds || 0;
    const bTime = b.startedAt?.seconds || b.createdAt?.seconds || 0;
    return bTime - aTime;
  });

  // Return only the most recent maxResults
  return executions.slice(0, maxResults);
};

// Notification Rules Management
export const getNotificationRules = async () => {
  const q = query(collection(db, 'platformConfig'), where('type', '==', 'notificationRule'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Banner Rules Management
export const getBannerRules = async () => {
  const q = query(collection(db, 'platformConfig'), where('type', '==', 'bannerRule'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Status Rules Management (State Machines)
export const getStatusRules = async () => {
  const q = query(collection(db, 'platformConfig'), where('type', '==', 'statusRule'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Audit Logs
export const getAuditLogs = async (maxResults = 200) => {
  // Fetch all and sort client-side to ensure we get most recent
  const q = query(collection(db, 'auditLogs'));
  const snapshot = await getDocs(q);
  const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by most recent first
  logs.sort((a, b) => {
    const aTime = a.timestamp?.seconds || a.createdAt?.seconds || 0;
    const bTime = b.timestamp?.seconds || b.createdAt?.seconds || 0;
    return bTime - aTime;
  });

  // Return only the most recent maxResults
  return logs.slice(0, maxResults);
};

// System Health Metrics
export const getSystemHealth = async () => {
  try {
    const now = new Date();
    const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const last24hTimestamp = Math.floor(last24h.getTime() / 1000);

    // Initialize with defaults
    let totalJobs = 0;
    let completedWorkflows = 0;
    let workflowSuccessRate = 0;
    let activeExecutions = 0;
    let avgReleaseTime = 0;
    let last24hExecutions = 0;
    let failedExecutions = 0;

    try {
      // Fetch recent workflow executions for success rate
      const executionsQuery = query(collection(db, 'workflowExecutions'));
      const executionsSnapshot = await getDocs(executionsQuery);
      const allExecutions = executionsSnapshot.docs.map(doc => doc.data());

      const recent24hExecutions = allExecutions.filter(e =>
        e.startedAt?.seconds >= last24hTimestamp
      );

      const completed = recent24hExecutions.filter(e => e.status === 'completed').length;
      const failed = recent24hExecutions.filter(e => e.status === 'failed').length;
      const inProgress = recent24hExecutions.filter(e => e.status === 'in_progress').length;

      workflowSuccessRate = recent24hExecutions.length > 0
        ? (completed / recent24hExecutions.length) * 100
        : 100; // Default to 100% if no executions

      activeExecutions = inProgress;
      completedWorkflows = allExecutions.filter(e => e.status === 'completed').length;
      last24hExecutions = recent24hExecutions.length;
      failedExecutions = failed;
    } catch (err) {
      console.error('Error fetching workflow executions:', err);
    }

    try {
      // Count total jobs
      const jobsQuery = query(collection(db, 'staffingRequests'));
      const jobsSnapshot = await getDocs(jobsQuery);
      totalJobs = jobsSnapshot.docs.length;
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }

    try {
      // Calculate avg payment release time (from escrowed to released)
      const paymentsQuery = query(collection(db, 'escrowPayments'), where('status', '==', 'released'));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      const releasedPayments = paymentsSnapshot.docs.map(doc => doc.data());

      if (releasedPayments.length > 0) {
        const totalTime = releasedPayments.reduce((sum, payment) => {
          const escrowed = payment.paidAt?.seconds || 0;
          const released = payment.releasedAt?.seconds || 0;
          return sum + (released - escrowed);
        }, 0);
        avgReleaseTime = (totalTime / releasedPayments.length) / 3600; // Convert to hours
      }
    } catch (err) {
      console.error('Error fetching released payments:', err);
    }

    return {
      workflowSuccessRate: workflowSuccessRate.toFixed(1),
      activeExecutions,
      totalJobs,
      completedWorkflows,
      avgPaymentReleaseTime: avgReleaseTime.toFixed(1),
      last24hExecutions,
      failedExecutions
    };
  } catch (error) {
    console.error('Error in getSystemHealth:', error);
    // Return default values if everything fails
    return {
      workflowSuccessRate: '0',
      activeExecutions: 0,
      totalJobs: 0,
      completedWorkflows: 0,
      avgPaymentReleaseTime: '0',
      last24hExecutions: 0,
      failedExecutions: 0
    };
  }
};

// ===== AI DASHBOARD FUNCTIONS =====

// Get AI Decisions (for monitoring AI behavior)
export const getAIDecisions = async (maxResults = 50) => {
  const q = query(collection(db, 'aiDecisions'), limit(maxResults));
  const snapshot = await getDocs(q);
  const decisions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by most recent first
  decisions.sort((a, b) => {
    const aTime = a.createdAt?.seconds || 0;
    const bTime = b.createdAt?.seconds || 0;
    return bTime - aTime;
  });

  return decisions;
};

// Get Rule Evolution Suggestions (AI-generated improvement suggestions)
export const getRuleEvolutions = async (maxResults = 20) => {
  const q = query(collection(db, 'ruleEvolutions'), limit(maxResults));
  const snapshot = await getDocs(q);
  const evolutions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by most recent first
  evolutions.sort((a, b) => {
    const aTime = a.createdAt?.seconds || 0;
    const bTime = b.createdAt?.seconds || 0;
    return bTime - aTime;
  });

  return evolutions;
};

// Approve AI Rule Suggestion
export const approveRuleEvolution = async (evolutionId, adminUserId, notes = '') => {
  await updateDoc(doc(db, 'ruleEvolutions', evolutionId), {
    status: 'approved',
    approvedBy: adminUserId,
    approvedAt: serverTimestamp(),
    adminNotes: notes,
    updatedAt: serverTimestamp()
  });
};

// Reject AI Rule Suggestion
export const rejectRuleEvolution = async (evolutionId, adminUserId, reason, notes = '') => {
  await updateDoc(doc(db, 'ruleEvolutions', evolutionId), {
    status: 'rejected',
    rejectedBy: adminUserId,
    rejectedAt: serverTimestamp(),
    rejectionReason: reason,
    adminNotes: notes,
    updatedAt: serverTimestamp()
  });
};

// Get AI Performance Metrics (Last 7 Days)
export const getAIPerformanceMetrics = async () => {
  try {
    const now = new Date();
    const last7days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const last7daysTimestamp = Math.floor(last7days.getTime() / 1000);

    // Fetch AI decisions from last 7 days
    const decisionsQuery = query(collection(db, 'aiDecisions'));
    const decisionsSnapshot = await getDocs(decisionsQuery);
    const allDecisions = decisionsSnapshot.docs.map(doc => doc.data());

    const recentDecisions = allDecisions.filter(d =>
      d.createdAt?.seconds >= last7daysTimestamp
    );

    // Calculate metrics
    const totalCalls = recentDecisions.length;
    const avgResponseTime = recentDecisions.length > 0
      ? recentDecisions.reduce((sum, d) => sum + (d.responseTime || 0), 0) / recentDecisions.length
      : 0;

    const totalCost = recentDecisions.reduce((sum, d) => sum + (d.cost || 0), 0);

    // Calculate accuracy rate (based on user feedback/approval)
    const decisionsWithFeedback = recentDecisions.filter(d => d.userFeedback !== undefined);
    const accuracyRate = decisionsWithFeedback.length > 0
      ? (decisionsWithFeedback.filter(d => d.userFeedback === 'approved').length / decisionsWithFeedback.length) * 100
      : 0;

    // Get pending approvals
    const evolutionsQuery = query(collection(db, 'ruleEvolutions'), where('status', '==', 'pending'));
    const evolutionsSnapshot = await getDocs(evolutionsQuery);
    const pendingApprovals = evolutionsSnapshot.docs.length;

    return {
      totalCalls,
      avgResponseTime: avgResponseTime.toFixed(2),
      totalCost: totalCost.toFixed(2),
      accuracyRate: accuracyRate.toFixed(1),
      pendingApprovals
    };
  } catch (error) {
    console.error('Error getting AI performance metrics:', error);
    return {
      totalCalls: 0,
      avgResponseTime: '0',
      totalCost: '0',
      accuracyRate: '0',
      pendingApprovals: 0
    };
  }
};

// ===== COMMUNITY FORUM FUNCTIONS =====

// Get all community posts (feedback, issues, suggestions)
export const getCommunityPosts = async (maxResults = 100) => {
  const q = query(
    collection(db, 'communityPosts'),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Get posts by category
export const getCommunityPostsByCategory = async (category, maxResults = 50) => {
  const q = query(
    collection(db, 'communityPosts'),
    where('category', '==', category),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Get posts by status (pending, reviewed, resolved)
export const getCommunityPostsByStatus = async (status, maxResults = 50) => {
  const q = query(
    collection(db, 'communityPosts'),
    where('status', '==', status),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Update post status (admin action)
export const updateCommunityPostStatus = async (postId, adminUserId, status, adminNotes = '') => {
  await updateDoc(doc(db, 'communityPosts', postId), {
    status,
    reviewedBy: adminUserId,
    reviewedAt: serverTimestamp(),
    adminNotes,
    updatedAt: serverTimestamp()
  });
};

// Flag post for moderation (AI detected inappropriate content)
export const flagCommunityPost = async (postId, reason, moderatedContent = null) => {
  await updateDoc(doc(db, 'communityPosts', postId), {
    flagged: true,
    flagReason: reason,
    moderatedContent,
    flaggedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Add AI analysis to post
export const addAIAnalysisToPost = async (postId, analysis) => {
  await updateDoc(doc(db, 'communityPosts', postId), {
    aiAnalysis: analysis,
    aiAnalyzedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Add admin reply to post
export const replyToCommunityPost = async (postId, adminUserId, replyText) => {
  await updateDoc(doc(db, 'communityPosts', postId), {
    adminReply: replyText,
    repliedBy: adminUserId,
    repliedAt: serverTimestamp(),
    status: 'replied',
    updatedAt: serverTimestamp()
  });
};

// Mark post as resolved
export const resolveCommunityPost = async (postId, adminUserId, resolutionNotes = '') => {
  await updateDoc(doc(db, 'communityPosts', postId), {
    status: 'resolved',
    resolvedBy: adminUserId,
    resolvedAt: serverTimestamp(),
    resolutionNotes,
    updatedAt: serverTimestamp()
  });
};

// ===== ADMIN MESSAGING FUNCTIONS =====

// Get all users (for selecting message recipients)
export const getAllUsers = async (maxResults = 100) => {
  const q = query(collection(db, 'users'), limit(maxResults));
  const snapshot = await getDocs(q);
  const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by most recent first
  users.sort((a, b) => {
    const aTime = a.createdAt?.seconds || 0;
    const bTime = b.createdAt?.seconds || 0;
    return bTime - aTime;
  });

  return users;
};

// Search users by name or email
export const searchUsers = async (searchTerm) => {
  const q = query(collection(db, 'users'));
  const snapshot = await getDocs(q);
  const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Filter by search term (name or email)
  const filtered = users.filter(user => {
    const name = user.displayName?.toLowerCase() || '';
    const email = user.email?.toLowerCase() || '';
    const search = searchTerm.toLowerCase();
    return name.includes(search) || email.includes(search);
  });

  return filtered;
};

// Send admin message to specific user
export const sendAdminMessage = async (adminUserId, recipientId, title, message, priority = 'default', category = 'support', data = {}) => {
  // Get admin name
  const adminDoc = await getDoc(doc(db, 'users', adminUserId));
  const adminName = adminDoc.exists() ? (adminDoc.data().displayName || 'NovoKerja Support') : 'NovoKerja Support';

  const messageData = {
    recipientId,
    senderId: adminUserId,
    senderName: adminName,
    title,
    message,
    priority,
    category,
    data,
    read: false,
    readAt: null,
    createdAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'adminMessages'), messageData);
  return { id: docRef.id, ...messageData };
};

// Get admin messages (all or for specific recipient)
export const getAdminMessages = async (recipientId = null, maxResults = 50) => {
  let q;
  if (recipientId) {
    q = query(
      collection(db, 'adminMessages'),
      where('recipientId', '==', recipientId),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );
  } else {
    q = query(
      collection(db, 'adminMessages'),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Create broadcast message
export const createBroadcastMessage = async (adminUserId, title, message, priority = 'default', category = 'announcement', targetAudience = 'all', expiresAt = null, data = {}) => {
  const broadcastData = {
    title,
    message,
    priority,
    category,
    targetAudience,
    active: true,
    expiresAt: expiresAt ? expiresAt : null,
    data,
    createdBy: adminUserId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'broadcastMessages'), broadcastData);
  return { id: docRef.id, ...broadcastData };
};

// Get broadcast messages
export const getBroadcastMessages = async (maxResults = 20) => {
  const q = query(
    collection(db, 'broadcastMessages'),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Update broadcast message
export const updateBroadcastMessage = async (broadcastId, updates) => {
  await updateDoc(doc(db, 'broadcastMessages', broadcastId), {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

// Delete broadcast message
export const deleteBroadcastMessage = async (broadcastId) => {
  await updateDoc(doc(db, 'broadcastMessages', broadcastId), {
    active: false,
    updatedAt: serverTimestamp()
  });
};

// Test echo - send message and check if it was created
export const testEcho = async (adminUserId) => {
  // Send test message to admin themselves
  const testMessage = await sendAdminMessage(
    adminUserId,
    adminUserId,
    '🧪 Echo Test',
    'This is a test message to verify the messaging system is working correctly!',
    'default',
    'test',
    { test: true, timestamp: Date.now() }
  );

  // Wait a bit
  await new Promise(resolve => setTimeout(resolve, 1000));

  // Try to read it back
  const messages = await getAdminMessages(adminUserId, 1);

  return {
    sent: testMessage,
    received: messages[0] || null,
    success: messages.length > 0 && messages[0].title === '🧪 Echo Test'
  };
};

// ===== TWO-FACTOR AUTHENTICATION (TOTP) FUNCTIONS =====

// Generate 2FA secret and QR code for enrollment
export const setup2FA = async (userEmail) => {
  // Generate a random secret
  const secret = authenticator.generateSecret();

  // Create OTP Auth URL for QR code
  const otpauthUrl = authenticator.keyuri(
    userEmail,
    'NovoKerja Admin',
    secret
  );

  // Generate QR code image
  const qrCode = await QRCode.toDataURL(otpauthUrl);

  return {
    secret,
    qrCode,
    otpauthUrl
  };
};

// Verify 2FA TOTP code
export const verify2FACode = (code, secret) => {
  try {
    return authenticator.verify({ token: code, secret });
  } catch (error) {
    console.error('Error verifying 2FA code:', error);
    return false;
  }
};

// Generate backup codes (10 single-use codes)
export const generateBackupCodes = () => {
  const codes = [];
  for (let i = 0; i < 10; i++) {
    // Generate random 8-character code
    const code = Math.random().toString(36).substring(2, 10).toUpperCase();
    codes.push(code);
  }
  return codes;
};

// Hash backup code for secure storage
export const hashBackupCode = (code) => {
  // Use crypto to hash backup codes
  const hash = createHash('sha256');
  hash.update(code);
  return hash.digest('hex');
};

// Enable 2FA for user
export const enable2FA = async (userId, secret, backupCodes) => {
  // Hash all backup codes before storage
  const hashedCodes = backupCodes.map(code => hashBackupCode(code));

  await updateDoc(doc(db, 'users', userId), {
    twoFactorEnabled: true,
    twoFactorSecret: secret, // In production, encrypt this!
    twoFactorMethod: 'totp',
    twoFactorBackupCodes: hashedCodes,
    twoFactorEnabledAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Disable 2FA for user
export const disable2FA = async (userId) => {
  await updateDoc(doc(db, 'users', userId), {
    twoFactorEnabled: false,
    twoFactorSecret: null,
    twoFactorMethod: null,
    twoFactorBackupCodes: null,
    twoFactorDisabledAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Check if user has 2FA enabled
export const check2FAStatus = async (userId) => {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) return { enabled: false };

  const userData = userDoc.data();
  return {
    enabled: userData.twoFactorEnabled || false,
    method: userData.twoFactorMethod || null,
    secret: userData.twoFactorSecret || null,
    hasBackupCodes: (userData.twoFactorBackupCodes?.length || 0) > 0
  };
};

// Verify backup code
export const verifyBackupCode = async (userId, code) => {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) return false;

  const userData = userDoc.data();
  const hashedCode = hashBackupCode(code);

  // Check if code exists in backup codes
  const backupCodes = userData.twoFactorBackupCodes || [];
  const codeIndex = backupCodes.indexOf(hashedCode);

  if (codeIndex === -1) return false;

  // Remove used backup code
  backupCodes.splice(codeIndex, 1);

  await updateDoc(doc(db, 'users', userId), {
    twoFactorBackupCodes: backupCodes,
    updatedAt: serverTimestamp()
  });

  return true;
};

// ===== BILLPLZ AUTOMATED PAYOUT FUNCTIONS =====

// Get pending payouts (escrow payments ready for payout)
export const getPendingPayouts = async () => {
  // Fetch all RELEASED escrow payments (ready for payout)
  const q = query(collection(db, 'escrowPayments'), where('status', '==', 'released'));
  const snapshot = await getDocs(q);
  const payouts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Sort by release date (oldest first - FIFO payout)
  payouts.sort((a, b) => {
    const aTime = a.releasedAt?.seconds || 0;
    const bTime = b.releasedAt?.seconds || 0;
    return aTime - bTime;
  });

  return payouts;
};

// Pay worker via Billplz Payment Order API (automated bank transfer)
// Legacy direct method — kept for backward compatibility
export const payWithBillplz = async (escrowId, payoutDetails) => {
  const {
    bankCode,
    bankAccountNumber,
    recipientName,
    amount,
    workerId,
    jobTitle
  } = payoutDetails;

  // Call the Cloud Function (asia-southeast1 region — Billplz rejects non-Asian IPs)
  const createPaymentOrder = httpsCallable(functionsAsia, 'createPaymentOrder');

  const result = await createPaymentOrder({
    bankCode,
    bankAccountNumber,
    recipientName,
    description: `Payout: ${jobTitle || 'Job Assignment'}`.substring(0, 200),
    amount,
    escrowId,
    workerId
  });

  return result.data;
};

// Pay worker via local relay (constructs request locally like manual scripts)
// Flow: Cloud Function validates + marks escrow → relay builds & sends to Billplz → Cloud Function verifies
export const payWithBillplzRelay = async (escrowId, payoutDetails) => {
  const {
    bankCode,
    bankAccountNumber,
    recipientName,
    amount,
    workerId,
    jobTitle
  } = payoutDetails;

  // Step 1: Validate + mark escrow as payout_initiated via Cloud Function
  const prepare = httpsCallable(functionsAsia, 'preparePaymentOrder');
  const { data: prepared } = await prepare({
    bankCode,
    bankAccountNumber,
    recipientName,
    description: `Payout: ${jobTitle || 'Job Assignment'}`.substring(0, 200),
    amount,
    escrowId,
    workerId
  });

  if (!prepared.success) {
    throw new Error(prepared.error || 'Failed to prepare payment order');
  }

  // Step 2: Relay constructs and sends request locally (like manual scripts)
  let relayResponse;
  try {
    relayResponse = await fetch('http://localhost:3737/payout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bankCode,
        bankAccountNumber,
        recipientName,
        description: `NovoKerja Payout - ${jobTitle || 'Job Assignment'}`,
        amount: prepared.workerPayout || amount,
        email: prepared.workerEmail || ''
      })
    });
  } catch (err) {
    throw new Error('Cannot reach local relay. Is payout-relay running? Start with: node scripts/payout-relay.js');
  }

  if (!relayResponse.ok) {
    const errorText = await relayResponse.text();
    throw new Error(`Relay error (${relayResponse.status}): ${errorText}`);
  }

  const billplzResult = await relayResponse.json();

  if (!billplzResult.id) {
    throw new Error(billplzResult.error?.message || billplzResult.error || JSON.stringify(billplzResult));
  }

  // Step 3: Verify + update Firestore via Cloud Function
  const complete = httpsCallable(functionsAsia, 'completePaymentOrder');
  const { data: result } = await complete({
    escrowId,
    paymentOrderId: billplzResult.id
  });

  return result;
};

// Malaysian bank codes for Billplz Payment Order
export const BILLPLZ_BANK_CODES = [
  { code: 'MBBEMYKL', name: 'Maybank' },
  { code: 'CIBBMYKL', name: 'CIMB Bank' },
  { code: 'PBBEMYKL', name: 'Public Bank' },
  { code: 'HLBBMYKL', name: 'Hong Leong Bank' },
  { code: 'RHBBMYKL', name: 'RHB Bank' },
  { code: 'ARBKMYKL', name: 'AmBank' },
  { code: 'BKRMMYKL', name: 'Bank Rakyat' },
  { code: 'BIMBMYKL', name: 'Bank Islam' },
  { code: 'MFBBMYKL', name: 'Alliance Bank' },
  { code: 'PHBMMYKL', name: 'Affin Bank' },
  { code: 'BMMBMYKL', name: 'Bank Muamalat' },
  { code: 'BSNAMYK1', name: 'BSN' },
  { code: 'SCBLMYKX', name: 'Standard Chartered' },
  { code: 'OCBCMYKL', name: 'OCBC Bank' },
  { code: 'AGOBMYKL', name: 'Agrobank' },
  { code: 'UOVBMYKL', name: 'UOB Bank' },
  { code: 'HBMBMYKL', name: 'HSBC' },
  { code: 'KFHOMYKL', name: 'Kuwait Finance House' },
  { code: 'RJHIMYKL', name: 'Al Rajhi Bank' },
  { code: 'CITIMYKL', name: 'Citibank' },
  { code: 'AFBQMYKL', name: 'MBSB Bank' }
];

// Get bank code from bank name
export const getBankCodeFromName = (bankName) => {
  if (!bankName) return null;
  const normalizedName = bankName.toLowerCase().trim();
  const bank = BILLPLZ_BANK_CODES.find(b =>
    normalizedName.includes(b.name.toLowerCase()) ||
    b.name.toLowerCase().includes(normalizedName)
  );
  return bank?.code || null;
};
